/**
 * bh-cart-integrity.js — keeps the cart's surcharge lines honest.
 *
 * Every surcharge (rush fee, delivery zone, premium filling, decoration add-on) is added as its
 * own line item carrying a `_Type` property, and the cart UI deliberately gives those lines no
 * remove control (see the .cart-item--fee rules in cart.css). That closes the hole where a
 * customer could delete the €5 rush fee, or a paid add-on, while the cake line still described
 * the thing they were no longer paying for — the order reached the kitchen already wrong.
 *
 * Which leaves one loose end: remove the CAKE and its fees are stranded in a cart with no way to
 * delete them.
 *
 * This used to clear the WHOLE cart whenever nothing but fees was left — the only option back
 * when fees were tied to their cake by `_Related_To: "Cake for <date>"`, i.e. the date alone,
 * which two cakes on the same day shared. Now every cake carries a unique `_Cake_ID` and its
 * surcharges carry the same token, so orphans can be identified exactly: remove one cake from a
 * two-cake cart and only ITS add-ons go with it, while the other cake keeps its own.
 *
 * The old whole-cart clear survives as a fallback for carts created before the token existed,
 * whose fee lines have no `_Cake_ID` to match on.
 */
(function () {
  // `_Type` is underscore-prefixed so Shopify's checkout hides it; it is still readable here.
  const isFeeLine = (item) => !!(item.properties && item.properties._Type);
  const cakeIdOf = (item) => (item.properties && item.properties._Cake_ID) || null;

  let busy = false;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function announce() {
    // Re-render the cart surfaces. Guarded by `busy`, so the refresh we trigger here re-enters
    // check(), finds nothing orphaned, and stops.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function check() {
    if (busy) return;
    busy = true;
    try {
      const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null));
      if (!cart || !cart.items.length) return;

      const fees = cart.items.filter(isFeeLine);
      if (!fees.length) return;

      // Tokens belonging to cakes still in the cart. A fee pointing at anything else is orphaned.
      const liveCakes = new Set(
        cart.items.filter((i) => !isFeeLine(i)).map(cakeIdOf).filter(Boolean)
      );

      const orphans = fees.filter((f) => {
        const id = cakeIdOf(f);
        return id && !liveCakes.has(id);
      });

      if (orphans.length) {
        const updates = {};
        orphans.forEach((o) => { updates[o.key] = 0; });
        await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        announce();
        return;
      }

      // Legacy fallback: pre-token carts whose fees carry no _Cake_ID cannot be matched to a
      // cake, so the only safe reading of "every remaining line is a fee" is that none is valid.
      const untokened = fees.filter((f) => !cakeIdOf(f));
      if (untokened.length && cart.items.every(isFeeLine)) {
        await fetch(`${root()}cart/clear.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        announce();
      }
    } catch (e) {
      console.warn('bh-cart-integrity: check failed', e);
    } finally {
      busy = false;
    }
  }

  // `cart:refresh` is the theme's post-mutation signal (assets/cart.js, app.js, cart-upsells).
  // Both listeners exist because the theme dispatches on `document` in some paths and on
  // `documentElement` in others.
  document.addEventListener('cart:refresh', check);
  document.documentElement.addEventListener('cart:refresh', check);
  // The cart page and the group delete re-render in place and signal `line-item:change:end`
  // instead of `cart:refresh` (assets/cart.js, assets/cart-remove.js), so listen there too.
  document.addEventListener('line-item:change:end', check);

  // Also on load: catches a cart left stranded in a previous session or another tab.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check);
  else check();
})();
